import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Wind, ArrowLeft } from "lucide-react";
import { useAuth } from "./AuthContext";
import { formatApiErrorDetail } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-steel bg-ink px-4 py-3 font-mono text-sm text-foreground placeholder:text-smoke/50 focus:border-volt focus:outline-none";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = mode === "register" ? { email, password, name } : { email, password };
      const { data } = await axios.post(`${API}/auth/${mode}`, payload, { withCredentials: true });
      setUser(data);
      nav(data.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-ink" data-testid="auth-page">
      <div className="spotlight absolute inset-0" aria-hidden="true" />
      <header className="relative z-10 border-b border-steel">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest" data-testid="auth-brand">
            <Wind className="h-5 w-5 text-volt" strokeWidth={2.5} />
            <span>X8<span className="text-volt">/</span>Violent Fan</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke hover:text-volt" data-testid="auth-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to store
          </Link>
        </div>
      </header>

      <main className="relative z-10 flex flex-1 items-center justify-center px-5 py-16">
        <div className="w-full max-w-md border border-steel bg-carbon p-8 md:p-10">
          <div className="flex border border-steel font-mono text-xs uppercase tracking-[0.2em]">
            <button
              data-testid="auth-tab-login"
              onClick={() => setMode("login")}
              className={`flex-1 py-3 transition-colors ${mode === "login" ? "bg-volt text-ink font-bold" : "text-smoke hover:text-volt"}`}
            >
              Sign in
            </button>
            <button
              data-testid="auth-tab-register"
              onClick={() => setMode("register")}
              className={`flex-1 py-3 transition-colors ${mode === "register" ? "bg-volt text-ink font-bold" : "text-smoke hover:text-volt"}`}
            >
              Create account
            </button>
          </div>

          <h1 className="mt-8 font-display text-2xl font-black uppercase tracking-tight">
            {mode === "login" ? "Welcome back" : "Join the crew"}
          </h1>
          <p className="mt-2 text-sm text-smoke">
            {mode === "login" ? "Sign in to track your orders." : "Faster checkout and order tracking."}
          </p>

          <form onSubmit={submit} className="mt-8 grid gap-3" data-testid="auth-form">
            {mode === "register" && (
              <input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Full name" className={inputCls} />
            )}
            <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email" className={inputCls} />
            <input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} placeholder="Password (8+ characters)" className={inputCls} />
            {error && <p data-testid="auth-error" className="font-mono text-xs text-red-400">{error}</p>}
            <button
              data-testid="auth-submit"
              type="submit"
              disabled={loading}
              className="mt-2 border border-volt bg-volt px-6 py-4 font-mono text-xs font-bold tracking-[0.2em] uppercase text-ink transition-colors duration-200 hover:bg-transparent hover:text-volt disabled:opacity-60"
            >
              {loading ? "One moment…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
